// ─────────────────────────────────────────────
//  TechnicalTab.js — Tab 2: Technical Analysis
//  Candlestick + SMA overlays + RSI + MACD + Volume
// ─────────────────────────────────────────────
import React, { useState, useEffect, useMemo } from "react";
import { fetchTimeSeries } from "../../config/api";
import CandlestickChart from "./CandlestickChart";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, ReferenceLine, CartesianGrid,
} from "recharts";
import "./TechnicalTab.css";

const PERIODS = [
  { label: "1W",  period: "1W",  interval: "1D" },
  { label: "1M",  period: "1M",  interval: "1D" },
  { label: "3M",  period: "3M",  interval: "1D" },
  { label: "6M",  period: "6M",  interval: "1W" },
  { label: "1Y",  period: "1Y",  interval: "1W" },
];
const SMA_OPTIONS = ["SMA20", "SMA50", "SMA200"];

function calcRSI(closes, period = 14) {
  if (closes.length < period + 1) return closes.map(() => null);
  const gains = [], losses = [];
  for (let i = 1; i < closes.length; i++) {
    const diff = closes[i] - closes[i - 1];
    gains.push(diff > 0 ? diff : 0);
    losses.push(diff < 0 ? Math.abs(diff) : 0);
  }
  const result = [null]; // first has no RSI
  let avgGain = gains.slice(0, period).reduce((a, b) => a + b, 0) / period;
  let avgLoss = losses.slice(0, period).reduce((a, b) => a + b, 0) / period;
  for (let i = period; i < gains.length; i++) {
    result.push(null); // align — fill leading nulls
  }
  // Simplified RSI from period onward
  const rsiArr = new Array(period).fill(null);
  let ag = gains.slice(0, period).reduce((a, b) => a + b, 0) / period;
  let al = losses.slice(0, period).reduce((a, b) => a + b, 0) / period;
  rsiArr.push(al === 0 ? 100 : 100 - 100 / (1 + ag / al));
  for (let i = period; i < gains.length; i++) {
    ag = (ag * (period - 1) + gains[i]) / period;
    al = (al * (period - 1) + losses[i]) / period;
    rsiArr.push(al === 0 ? 100 : 100 - 100 / (1 + ag / al));
  }
  return rsiArr;
}

function calcMACD(closes, fast = 12, slow = 26, signal = 9) {
function ema(arr, period) {
  if (arr.length < period) return new Array(arr.length).fill(null); // Return nulls if not enough data
  const k = 2 / (period + 1);
  const result = new Array(period - 1).fill(null);
  
  // Seed first value with simple moving average
  const seed = arr.slice(0, period).reduce((a, b) => a + b, 0) / period;
  result.push(seed);
  
  for (let i = period; i < arr.length; i++) {
    result.push(arr[i] * k + result[result.length - 1] * (1 - k));
  }
  return result;
}
  const emaFast = ema(closes, fast);
  const emaSlow = ema(closes, slow);
  const macdLine = closes.map((_, i) =>
    emaFast[i] != null && emaSlow[i] != null ? emaFast[i] - emaSlow[i] : null
  );
  const validMacd = macdLine.filter((v) => v != null);
  const signalLine = ema(validMacd, signal);
  let signalIdx = 0;
  const signalFull = macdLine.map((v) => (v != null ? signalLine[signalIdx++] ?? null : null));
  const histogram = macdLine.map((v, i) =>
    v != null && signalFull[i] != null ? v - signalFull[i] : null
  );
  return { macdLine, signalLine: signalFull, histogram };
}

function detectSupportResistance(candles) {
  if (candles.length < 20) return { support: [], resistance: [] };
  const closes = candles.map((c) => c.close);
  const window = 5;
  const support = [], resistance = [];
  
  for (let i = window; i < closes.length - window; i++) {
    const slice = closes.slice(i - window, i + window + 1);
    const min = Math.min(...slice);
    const max = Math.max(...slice);
    if (closes[i] === min) support.push(closes[i]);
    if (closes[i] === max) resistance.push(closes[i]);
  }

  const cluster = (levels) => {
    if (!levels.length) return [];
    const sorted = [...new Set(levels)].sort((a, b) => a - b);
    const result = [];
    let group = [sorted[0]];
    
    for (let i = 1; i < sorted.length; i++) {
      // Use 1.5% as a more robust clustering threshold
      if (sorted[i] - group[group.length - 1] < sorted[i] * 0.015) {
        group.push(sorted[i]);
      } else {
        result.push(group.reduce((a, b) => a + b, 0) / group.length);
        group = [sorted[i]];
      }
    }
    result.push(group.reduce((a, b) => a + b, 0) / group.length);
    return result.filter(v => !isNaN(v)).slice(-3); // Get latest 3 valid levels
  };

  return { support: cluster(support), resistance: cluster(resistance) };
}

const CustomRsiTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const v = payload[0]?.value;
  return (
    <div className="tt-mini">
      RSI: <span style={{ color: v > 70 ? "#ef4444" : v < 30 ? "#22c55e" : "#94a3b8" }}>
        {v?.toFixed(1) ?? "—"}
      </span>
    </div>
  );
};

const CustomMacdTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="tt-mini">
      {payload.map((p) => (
        <div key={p.name} style={{ color: p.color }}>
          {p.name}: {p.value?.toFixed(3) ?? "—"}
        </div>
      ))}
    </div>
  );
};

export default function TechnicalTab({ symbol, exchange }) {
  const [selected, setSelected] = useState(PERIODS[1]);
  const [overlays,  setOverlays] = useState(["SMA20", "SMA50"]);
  const [candles,   setCandles]  = useState([]);
  const [loading,   setLoading]  = useState(true);
  const [error,     setError]    = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError(null);
      try {
        const data = await fetchTimeSeries(symbol, exchange, selected.period, selected.interval);
        if (!cancelled) {
          // Normalise field names — RapidAPI returns varying structures
          const raw = data?.time_series || data?.candles || data || [];
          const normalised = raw.map((c) => ({
            date:   c.date || c.datetime || c.time || "",
            open:   parseFloat(c.open)   || 0,
            high:   parseFloat(c.high)   || 0,
            low:    parseFloat(c.low)    || 0,
            close:  parseFloat(c.close)  || 0,
            volume: parseFloat(c.volume) || 0,
          })).filter((c) => c.close > 0);
          setCandles(normalised);
        }
      } catch (e) {
        if (!cancelled) setError("Could not load chart data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [symbol, exchange, selected]);

  const { rsiData, macdData, srLevels } = useMemo(() => {
    if (!candles.length) return { rsiData: [], macdData: [], srLevels: { support: [], resistance: [] } };
    const closes = candles.map((c) => c.close);
    const rsiArr  = calcRSI(closes);
    const { macdLine, signalLine, histogram } = calcMACD(closes);
    return {
      rsiData: candles.map((c, i) => ({
        date: c.date, rsi: rsiArr[i],
      })),
      macdData: candles.map((c, i) => ({
        date: c.date, macd: macdLine[i], signal: signalLine[i], hist: histogram[i],
      })),
      srLevels: detectSupportResistance(candles),
    };
  }, [candles]);

  function toggleOverlay(name) {
    setOverlays((prev) =>
      prev.includes(name) ? prev.filter((o) => o !== name) : [...prev, name]
    );
  }

  const currentRsi = rsiData.filter((d) => d.rsi != null).slice(-1)[0]?.rsi;
  const rsiSignal  = currentRsi > 70 ? "Overbought" : currentRsi < 30 ? "Oversold" : "Neutral";
  const rsiColor   = currentRsi > 70 ? "#ef4444" : currentRsi < 30 ? "#22c55e" : "#94a3b8";

  return (
    <div className="tt-wrap">
      {/* ── Controls ── */}
      <div className="tt-controls">
        <div className="tt-period-group">
          {PERIODS.map((p) => (
            <button
              key={p.label}
              className={`tt-period-btn ${selected.label === p.label ? "active" : ""}`}
              onClick={() => setSelected(p)}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="tt-overlay-group">
          {SMA_OPTIONS.map((sma) => {
            const colors = { SMA20: "#f59e0b", SMA50: "#3b82f6", SMA200: "#a855f7" };
            return (
              <button
                key={sma}
                className={`tt-overlay-btn ${overlays.includes(sma) ? "active" : ""}`}
                style={overlays.includes(sma) ? { borderColor: colors[sma], color: colors[sma] } : {}}
                onClick={() => toggleOverlay(sma)}
              >
                {sma}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Loading / Error ── */}
      {loading && (
        <div className="tt-loading">
          <div className="tt-loading-bar" />
          <p>Loading chart data…</p>
        </div>
      )}
      {error && <div className="tt-error">⚠ {error}</div>}

      {!loading && !error && (
        <>
          {/* ── Candlestick Chart ── */}
          <CandlestickChart candles={candles} overlays={overlays} height={380} />

          {/* ── Support & Resistance levels ── */}
          {(srLevels.support.length > 0 || srLevels.resistance.length > 0) && (
            <div className="tt-sr-section">
              <div className="tt-sr-title">Support &amp; Resistance Levels</div>
              <div className="tt-sr-pills">
                {srLevels.resistance.map((v, i) => (
                  <span key={`r${i}`} className="tt-sr-pill resistance">
                    R₹{v.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                  </span>
                ))}
                {srLevels.support.map((v, i) => (
                  <span key={`s${i}`} className="tt-sr-pill support">
                    S₹{v.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ── RSI Chart ── */}
          <div className="tt-indicator-section">
            <div className="tt-indicator-header">
              <span className="tt-indicator-title">RSI (14)</span>
              {currentRsi != null && (
                <span className="tt-indicator-val" style={{ color: rsiColor }}>
                  {currentRsi.toFixed(1)} — {rsiSignal}
                </span>
              )}
            </div>
            <ResponsiveContainer width="100%" height={100}>
              <LineChart data={rsiData} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                <CartesianGrid stroke="#1e2d45" strokeDasharray="3 3" vertical={false} />
                <YAxis domain={[0, 100]} tick={{ fill: "#334155", fontSize: 9 }} width={28} />
                <XAxis dataKey="date" hide />
                <Tooltip content={<CustomRsiTooltip />} />
                <ReferenceLine y={70} stroke="#ef4444" strokeDasharray="3 3" strokeWidth={0.8} />
                <ReferenceLine y={30} stroke="#22c55e" strokeDasharray="3 3" strokeWidth={0.8} />
                <ReferenceLine y={50} stroke="#2d3f59" strokeDasharray="2 4" strokeWidth={0.6} />
                <Line type="monotone" dataKey="rsi" stroke="#f59e0b" dot={false} strokeWidth={1.5} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* ── MACD Chart ── */}
          <div className="tt-indicator-section">
            <div className="tt-indicator-header">
              <span className="tt-indicator-title">MACD (12, 26, 9)</span>
            </div>
            <ResponsiveContainer width="100%" height={100}>
              <BarChart data={macdData} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                <CartesianGrid stroke="#1e2d45" strokeDasharray="3 3" vertical={false} />
                <YAxis tick={{ fill: "#334155", fontSize: 9 }} width={40} />
                <XAxis dataKey="date" hide />
                <Tooltip content={<CustomMacdTooltip />} />
                <ReferenceLine y={0} stroke="#2d3f59" strokeWidth={0.8} />
                <Bar dataKey="hist" name="Histogram"
                  fill="#3b82f6"
                  radius={[1,1,0,0]}
                  maxBarSize={4}
                  // colour each bar green/red based on value
                  label={false}
                >
                  {macdData.map((entry, i) => (
                    <rect key={i} fill={entry.hist >= 0 ? "#22c55e" : "#ef4444"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            {/* MACD line + signal as separate line chart */}
            <ResponsiveContainer width="100%" height={60}>
              <LineChart data={macdData} margin={{ top: 0, right: 8, bottom: 4, left: 0 }}>
                <YAxis tick={{ fill: "#334155", fontSize: 9 }} width={40} />
                <XAxis dataKey="date" hide />
                <Tooltip content={<CustomMacdTooltip />} />
                <ReferenceLine y={0} stroke="#2d3f59" strokeWidth={0.6} />
                <Line type="monotone" dataKey="macd"   name="MACD"   stroke="#3b82f6" dot={false} strokeWidth={1.2} connectNulls />
                <Line type="monotone" dataKey="signal" name="Signal" stroke="#f97316" dot={false} strokeWidth={1.2} connectNulls strokeDasharray="3 2" />
              </LineChart>
            </ResponsiveContainer>
            <div className="tt-macd-legend">
              <span style={{ color: "#22c55e" }}>█ Bullish Hist</span>
              <span style={{ color: "#ef4444" }}>█ Bearish Hist</span>
              <span style={{ color: "#3b82f6" }}>── MACD</span>
              <span style={{ color: "#f97316" }}>- - Signal</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
